'use client';
/**
 * Le JOURNAL, au même endroit partout.
 *
 * ── Le défaut qu'il comble ──────────────────────────────────────────────────
 *
 * Le journal existe pour presque tout ce que le module écrit — il est chaîné,
 * signé, opposable. Mais il s'atteint DIFFÉREMMENT selon l'écran : un onglet
 * sur la non-conformité, un bloc replié sur la suggestion, une section en bas
 * sur le document, et rien du tout ailleurs alors que les événements sont bien
 * là. Retour de la RQ du 18/08 : « partout où il y a un journal, une icône
 * s'affiche à la fenêtre toujours au même endroit et donne accès au journal
 * dans un popup. »
 *
 * Le gain n'est pas le confort. Aujourd'hui, ne pas trouver le journal d'un
 * objet ne dit rien : est-ce qu'il n'y en a pas, ou est-ce que je ne l'ai pas
 * trouvé ? Avec une place unique, la question disparaît — et **l'absence
 * d'icône devient une information**.
 *
 * ── Pourquoi rien plutôt qu'un bouton grisé ─────────────────────────────────
 *
 * Un bouton grisé se lit « ça devrait marcher mais ça ne marche pas » : on
 * clique, on repasse, on cherche pourquoi. Un bouton ABSENT se lit « il n'y a
 * rien à voir ici », ce qui est exactement le fait. C'est la même règle que
 * pour le bouton « Ouvrir en pleine page » du panneau des fiches liées.
 *
 * Corollaire : il faut donc SAVOIR si le journal est vide avant de décider, et
 * cela suppose de l'avoir lu. Le magasin met les journaux en cache par
 * référence : les écrans qui l'affichent déjà (NC, actions, réclamations) ne
 * paient aucune requête de plus, les autres en paient une, à l'ouverture de la
 * fiche.
 *
 * ── Ce qu'il ne fait pas ────────────────────────────────────────────────────
 *
 * Il ne remplace pas les onglets « Journal » existants : sur une fiche qu'on
 * examine longuement, le journal côte à côte avec le reste vaut mieux qu'un
 * aller-retour en fenêtre. Il donne un accès CONSTANT, pas l'accès unique.
 */
import React from 'react';
import { Bouton } from '@/shared/Bouton';
import { useGed } from '@/lib/store';
import { I, Modale } from '@/components/ui';
import { JournalFiche } from '@/components/JournalFiche';

export function BoutonJournal({ reference, quoi, evenements, heritage }: {
  /** Référence LISIBLE de l'objet — c'est la clé du journal. */
  reference: string;
  /** Nommé dans l'infobulle et en tête de la fenêtre : « de cette action ». */
  quoi?: string;
  /** Journal déjà chargé par l'écran : évite une seconde lecture des mêmes
   *  lignes, et permet de décider tout de suite s'il y a quelque chose. */
  evenements?: unknown[];
  /** Ancienne clé de chaînage (classeur papier) dont l'historique se lit
   *  encore, fusionné avant la clé courante — cf. `loadJournal`. */
  heritage?: string;
}) {
  const { loadJournal, events } = useGed();
  const fourni = Array.isArray(evenements);
  const [ouvert, setOuvert] = React.useState(false);
  const [lu, setLu] = React.useState(fourni);

  React.useEffect(() => {
    if (!reference || fourni) return;
    let vivant = true;
    void loadJournal(reference, heritage).finally(() => { if (vivant) setLu(true); });
    return () => { vivant = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [reference, fourni]);

  const journal = (fourni ? (evenements as unknown[]) : (events[reference] || [])) as unknown[];

  // Tant qu'on ne sait pas, on n'affiche RIEN — surtout pas un bouton qui
  // apparaîtrait puis disparaîtrait, ce qui déplacerait la ligne sous le
  // curseur au moment où l'on va cliquer ailleurs.
  if (!reference || !lu || journal.length === 0) return null;

  return (
    <>
      <Bouton taille="sm" onClick={() => setOuvert(true)}
        title={`Journal ${quoi || 'de cette fiche'} — ${journal.length} événement${journal.length > 1 ? 's' : ''}`}
        aria-label={`Ouvrir le journal ${quoi || 'de cette fiche'}`}>
        <I n="journal" s={13} />
        {journal.length}
      </Bouton>
      {ouvert && (
        <Modale
          /* `sansSaisie` : un journal chaîné se LIT. Le clic à côté y referme,
             comme avant (arbitrage RQ du 04/09/2026 sur les fenêtres purement
             consultatives). Rien ne s'y perd, donc rien n'a à se demander. */
          titre={`Journal — ${reference}`} onClose={() => setOuvert(false)} largeur={760} sansSaisie>
          {/* La RÉFÉRENCE en tête de la fenêtre, pas seulement le mot
              « Journal » : ouverte par-dessus un écran qui en liste vingt, une
              fenêtre qui ne dit pas de quoi elle parle oblige à la refermer
              pour vérifier. */}
          {/* Le journal DÉJÀ LU est repassé tel quel : rechargé par
              `JournalFiche`, il perdrait l'héritage — celui-ci n'est fusionné
              que par l'appel de CE composant. */}
          <JournalFiche code={reference} compact evenements={journal as never[]} />
        </Modale>
      )}
    </>
  );
}
